import { useEffect, useState } from "react";
import { confirmAction } from "../../utils/confirm";
import AppModal from "../../components/AppModal";
import PhoneInput from "../../components/PhoneInput";
import Pagination from "../../components/Pagination/Pagination";
import FilterBar, { FilterField } from "../../components/FilterBar";
import { useListController } from "../../controllers/useListController";
import {
  usersService,
  lookupsService,
} from "../../services/apiService";
import { usePermission } from "../../hooks/usePermission";
import { useAuthStore } from "../../store/authStore";
import { showToast } from "../../utils/toast";
import { validateForm } from "../../utils/validate";
import { formatPhone, isValidPhone, PHONE_DIGITS } from "../../utils/phone";
import { ACTIVE_STATUS_FILTERS, emptyMessageForStatus, statusFromFilter } from "../../utils/listFilters";

const STATUS_FILTERS = ACTIVE_STATUS_FILTERS("All users");

const EMPTY = {
  id: null,
  full_name: "",
  email: "",
  phone: "",
  password: "",
  role_id: "",
  centre_id: "",
  is_active: 1,
};

export default function Users() {
  const { rows, pagination, loading, error, fetchRows } =
    useListController(usersService);
  const { can, isGroupAdmin } = usePermission();
  const currentUser = useAuthStore((s) => s.user);
  const canAdd = can("users", "is_add");
  const canEdit = can("users", "is_update");
  const canDelete = can("users", "is_delete");

  const [roles, setRoles] = useState([]);
  const [centres, setCentres] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [viewFilter, setViewFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("");
  const [search, setSearch] = useState("");

  const queryParams = (extra = {}) => ({
    page: extra.page ?? pagination.page,
    limit: extra.limit ?? pagination.limit,
    status: statusFromFilter(extra.filter ?? viewFilter, STATUS_FILTERS),
    role_id: extra.role_id ?? roleFilter,
    search: extra.search ?? search,
  });

  useEffect(() => {
    lookupsService.getRoles().then((res) => setRoles(res?.data || []));
    lookupsService.getCentres().then((res) => setCentres(res?.data || []));
  }, []);

  useEffect(() => {
    fetchRows({
      page: 1,
      status: statusFromFilter(viewFilter, STATUS_FILTERS),
      role_id: roleFilter,
      search: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFilter, roleFilter]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchRows({
      page: 1,
      status: statusFromFilter(viewFilter, STATUS_FILTERS),
      role_id: roleFilter,
      search,
    });
  };

  const onReset = (e) => {
    e.preventDefault();
    setViewFilter("all");
    setRoleFilter("");
    setSearch("");
    fetchRows({ page: 1, status: "", role_id: "", search: "" });
  };

  const handleStatusChange = (e) => {
    setViewFilter(e.target.value);
    setSearch("");
  };

  const handleRoleChange = (e) => {
    setRoleFilter(e.target.value);
    setSearch("");
  };

  const emptyMessage = emptyMessageForStatus(viewFilter, {
    all: "No users yet",
    active: "No active users",
    inactive: "No inactive users",
  });

  const roleById = (id) => roles.find((r) => String(r.id) === String(id));
  const isCentreRole = (id) => (roleById(id)?.name || "") === "centre_admin";

  const openAdd = () => {
    setForm(
      isGroupAdmin
        ? EMPTY
        : { ...EMPTY, centre_id: currentUser?.centre_id || "" }
    );
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setForm({
      ...EMPTY,
      ...row,
      phone: formatPhone(row.phone || ""),
      role_id: row.role_id || "",
      centre_id: row.centre_id || "",
    });
    setModalOpen(true);
  };

  const openChangePassword = (row) => {
    setPasswordUser({ id: row.id, full_name: row.full_name });
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordModalOpen(true);
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? (checked ? 1 : 0) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !validateForm(e.currentTarget, [
        { name: "full_name", value: form.full_name, label: "Full name", required: true },
        { name: "email", value: form.email, label: "Email", required: true, type: "email" },
        { name: "phone", value: form.phone, label: "Phone",
          custom: (v) =>
            String(v || "").trim() && !isValidPhone(v)
              ? `Enter ${PHONE_DIGITS} digits, or leave blank`
              : null,
        },
        { name: "role_id", value: form.role_id, label: "Role", required: true },
        ...(form.id
          ? []
          : [
              {
                name: "password",
                value: form.password,
                label: "Password",
                required: true,
                minLength: 6,
              },
            ]),
        {
          name: "centre_id",
          value: form.centre_id,
          label: "Centre",
          custom: () =>
            isCentreRole(form.role_id) && !form.centre_id
              ? "A Centre Admin must be assigned to a centre"
              : null,
        },
      ])
    )
      return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!isCentreRole(form.role_id)) payload.centre_id = "";
      // A centre admin can only assign users to their own centre.
      else if (!isGroupAdmin) payload.centre_id = currentUser?.centre_id || "";
      if (form.id) delete payload.password;
      if (payload.phone?.trim()) payload.phone = formatPhone(payload.phone);
      else payload.phone = null;

      const res = form.id
        ? await usersService.update(form.id, payload)
        : await usersService.create(payload);
      if (res?.success) {
        showToast("success", form.id ? "User updated" : "User created");
        setModalOpen(false);
        fetchRows(queryParams());
      } else showToast("error", res?.msg || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    if (!passwordUser?.id) return;
    if (
      !validateForm(e.currentTarget, [
        { name: "new_password", value: newPassword, label: "New password", required: true, minLength: 6 },
        { name: "confirm_password", value: confirmPassword, label: "Confirm password", required: true,
          custom: (v) => (v && v !== newPassword ? "Those passwords don't match" : null) },
      ])
    ) {
      if (newPassword.length >= 6 && newPassword !== confirmPassword) {
        setPasswordError("Those passwords don't match.");
      }
      return;
    }

    setPasswordSubmitting(true);
    try {
      const res = await usersService.update(passwordUser.id, { password: newPassword });
      if (res?.success) {
        showToast("success", "Password updated");
        setPasswordModalOpen(false);
        setPasswordUser(null);
      } else {
        setPasswordError(res?.msg || "Failed to update password.");
      }
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    const result = await confirmAction({
      title: `Delete ${row.full_name}?`,
      icon: "warning",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!result.isConfirmed) return;
    const res = await usersService.delete(row.id);
    if (res?.success) {
      showToast("success", "User deleted");
      fetchRows(queryParams());
    } else showToast("error", res?.msg || "Failed");
  };

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>Admin Users</strong>
        {canAdd && (
          <button className="btn btn-primary" onClick={openAdd}>
            <i className="cil-plus me-1" /> Add User
          </button>
        )}
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}

        <FilterBar onSubmit={onSearch} onReset={onReset}>
          <FilterField id="user-status-filter" label="Show">
            <select
              id="user-status-filter"
              className="form-select"
              value={viewFilter}
              onChange={handleStatusChange}
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField id="user-role-filter" label="Role">
            <select
              id="user-role-filter"
              className="form-select"
              value={roleFilter}
              onChange={handleRoleChange}
            >
              <option value="">All roles</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name === "group_admin" ? "Group Admin" : r.name === "centre_admin" ? "Centre Admin" : r.name}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField id="user-search" label="Search">
            <input
              id="user-search"
              className="form-control"
              placeholder="Name, email or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterField>
        </FilterBar>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Centre</th>
                <th>Status</th>
                <th style={{ width: 220 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={6} className="text-center py-4">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-4 text-muted">{emptyMessage}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.id}>
                    <td className="fw-semibold">{r.full_name}</td>
                    <td>{r.email}</td>
                    <td>
                      <span className="badge bg-primary">
                        {r.role_name === "group_admin" ? "Group Admin" : "Centre Admin"}
                      </span>
                    </td>
                    <td>{r.centre_name || "All centres"}</td>
                    <td>
                      {r.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {canEdit && (
                          <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(r)}>Edit</button>
                        )}
                        {canEdit && (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => openChangePassword(r)}
                          >
                            <i className="cil-lock-locked me-1" />
                            Password
                          </button>
                        )}
                        {canDelete && (
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          itemsCount={rows.length}
          loading={loading}
          onPageChange={(page) => fetchRows({ ...queryParams(), page })}
          onLimitChange={(limit) => fetchRows({ ...queryParams(), page: 1, limit })}
        />
      </div>

      <AppModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={form.id ? "Edit User" : "Add User"}
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" form="user-form" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Saving…" : form.id ? "Save Changes" : "Create"}
            </button>
          </>
        }
      >
              <form id="user-form" onSubmit={handleSubmit} noValidate>
                <div className="mb-3">
                  <label className="form-label">Full name <span className="text-danger">*</span></label>
                  <input name="full_name" className="form-control" value={form.full_name} onChange={onChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Email <span className="text-danger">*</span></label>
                  <input name="email" type="email" className="form-control" value={form.email} onChange={onChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Phone <span className="text-muted small">(optional, 10 digits)</span></label>
                  <PhoneInput name="phone" value={form.phone} onChange={onChange} />
                </div>
                {!form.id && (
                  <div className="mb-3">
                    <label className="form-label">
                      Password <span className="text-danger">*</span>
                    </label>
                    <input
                      name="password"
                      type="password"
                      className="form-control"
                      value={form.password}
                      onChange={onChange}
                      required
                      minLength={6}
                    />
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label">Role <span className="text-danger">*</span></label>
                  <select name="role_id" className="form-select" value={form.role_id || ""} onChange={onChange} required>
                    <option value="">— Select role —</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name === "group_admin" ? "Group Admin" : r.name === "centre_admin" ? "Centre Admin" : r.name}
                      </option>
                    ))}
                  </select>
                </div>
                {isCentreRole(form.role_id) && (
                  <div className="mb-3">
                    <label className="form-label">Centre <span className="text-danger">*</span></label>
                    <select name="centre_id" className="form-select" value={form.centre_id || ""} onChange={onChange} disabled={!isGroupAdmin}>
                      <option value="">— Select centre —</option>
                      {centres.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    {!isGroupAdmin && (
                      <div className="form-text">
                        Centre admins can only assign users to their own centre.
                      </div>
                    )}
                  </div>
                )}
                <div className="form-check">
                  <input type="checkbox" id="userActive" className="form-check-input" name="is_active" checked={!!form.is_active} onChange={onChange} />
                  <label htmlFor="userActive" className="form-check-label ms-1">Active</label>
                </div>
              </form>
      </AppModal>

      <AppModal
        open={passwordModalOpen}
        onClose={() => {
          setPasswordModalOpen(false);
          setPasswordUser(null);
        }}
        title={`Change password — ${passwordUser?.full_name || "User"}`}
        size="sm"
        stacked
        footer={
          <>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setPasswordModalOpen(false);
                setPasswordUser(null);
              }}
              disabled={passwordSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="user-password-form"
              className="btn btn-primary"
              disabled={passwordSubmitting}
            >
              {passwordSubmitting ? "Saving…" : "Update password"}
            </button>
          </>
        }
      >
        {passwordError && <div className="alert alert-danger py-2">{passwordError}</div>}
        <form id="user-password-form" onSubmit={handlePasswordSubmit} noValidate>
          <div className="mb-3">
            <label className="form-label">New password</label>
            <input
              type="password"
              name="new_password"
              className="form-control"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 6 characters"
              autoComplete="new-password"
              required
              minLength={6}
            />
          </div>
          <div className="mb-0">
            <label className="form-label">Confirm new password</label>
            <input
              type="password"
              name="confirm_password"
              className="form-control"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              autoComplete="new-password"
              required
            />
          </div>
        </form>
      </AppModal>
    </div>
  );
}
